import React from 'react';
import { Switch, Text, TextInput, View } from 'react-native';
import { formatUnitLabel, normalizeUnitOfMeasure } from '@shared/quantity';
import { ticketColors } from '../../shared/theme/ticketColors';
import { catalogProductFormStyles as styles } from './catalogProductForm.styles';
import type { CatalogProductFormFieldsProps } from './catalogProductFormFields.types';

type Props = Pick<
  CatalogProductFormFieldsProps,
  | 'draft'
  | 'onPublicDescriptionChange'
  | 'onMinOrderQtyChange'
  | 'onMaxOrderQtyChange'
  | 'onIsPublicChange'
  | 'onMenuAvailableChange'
  | 'onVisibleOnMenuCartaChange'
  | 'categoryOptions'
  | 'mobile'
  | 'desktopRail'
> & {
  onlineStoreEnabled: boolean;
  menuQrEnabled: boolean;
  posEnabled: boolean;
};

export function CatalogProductFormPublicStoreFields({
  draft,
  onPublicDescriptionChange,
  onMinOrderQtyChange,
  onMaxOrderQtyChange,
  onIsPublicChange,
  onMenuAvailableChange,
  onVisibleOnMenuCartaChange,
  categoryOptions,
  onlineStoreEnabled,
  menuQrEnabled,
  posEnabled,
  mobile = false,
  desktopRail = false,
}: Props) {
  const selectedCategory = categoryOptions.find((c) => c.id === draft.categoryId);
  const isCategoryRestricted = selectedCategory?.isRestricted ?? false;
  const menuQrVisible = menuQrEnabled && draft.itemType === 'physical';
  const showOrderQtyFields = onlineStoreEnabled && draft.isPublic && draft.itemType === 'physical';
  const uom = normalizeUnitOfMeasure(draft.unitOfMeasure);
  const unitLabel = uom === 'unit' ? 'unidades' : formatUnitLabel(uom);
  const availabilityHint =
    onlineStoreEnabled && menuQrVisible && posEnabled
      ? 'Si lo apagás, no se vende en la tienda online, la carta QR ni el mostrador.'
      : onlineStoreEnabled && menuQrVisible
        ? 'Si lo apagás, no se vende ni en la tienda online ni en la carta QR.'
        : onlineStoreEnabled && posEnabled
          ? 'Si lo apagás, no se vende ni en la tienda online ni en el mostrador.'
          : menuQrVisible && posEnabled
            ? 'Si lo apagás, no se vende ni en la carta QR ni en el mostrador.'
            : onlineStoreEnabled
              ? 'Si lo apagás, no se vende en la tienda online.'
              : menuQrVisible
                ? 'Si lo apagás, no se vende en la carta QR.'
                : 'Si lo apagás, no se vende en el mostrador.';

  return (
    <View style={styles.ticketFormSection}>
      {!desktopRail ? <Text style={styles.ticketFormSectionTitle}>Disponibilidad</Text> : null}
      {onlineStoreEnabled ? (
        <View style={[styles.ticketFormField, { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }]}>
          <Text style={styles.ticketFormLabel}>Se muestra en la tienda online</Text>
          <Switch
            testID="catalog.form.public.online"
            value={draft.isPublic}
            onValueChange={onIsPublicChange ?? (() => {})}
            trackColor={{ false: ticketColors.ink3, true: ticketColors.ok }}
          />
        </View>
      ) : null}
      {menuQrVisible ? (
        <View style={[styles.ticketFormField, { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }]}>
          <View style={{ flex: 1, paddingRight: 12 }}>
            <Text style={styles.ticketFormLabel}>Se muestra en la carta QR</Text>
            <Text style={[styles.ticketFormLabel, { fontSize: 11, opacity: 0.7, fontWeight: '400' }]}>
              {isCategoryRestricted
                ? 'Los productos +18 no aparecen en esta carta.'
                : 'La carta de la mesa es independiente de la tienda online.'}
            </Text>
          </View>
          <Switch
            testID="catalog.form.public.menu-carta"
            value={!isCategoryRestricted && draft.visibleOnMenuCarta !== false}
            onValueChange={onVisibleOnMenuCartaChange ?? (() => {})}
            trackColor={{ false: ticketColors.ink3, true: ticketColors.ok }}
            disabled={isCategoryRestricted}
          />
        </View>
      ) : null}
      <View style={[styles.ticketFormField, { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }]}>
        <View style={{ flex: 1, paddingRight: 12 }}>
          <Text style={styles.ticketFormLabel}>Disponible hoy</Text>
          <Text style={[styles.ticketFormLabel, { fontSize: 11, opacity: 0.7, fontWeight: '400' }]}>
            {availabilityHint}
          </Text>
        </View>
        <Switch
          testID="catalog.form.public.available-today"
          value={draft.menuAvailable !== false}
          onValueChange={onMenuAvailableChange ?? (() => {})}
          trackColor={{ false: ticketColors.ink3, true: ticketColors.ok }}
        />
      </View>
      {onlineStoreEnabled && isCategoryRestricted ? (
        <View style={[styles.imageWarnNotice, { marginTop: 4 }]}>
          <Text style={styles.imageWarnNoticeText}>
            La tienda online pide confirmar que el cliente es mayor de 18 al comprar.
          </Text>
        </View>
      ) : null}
      {onlineStoreEnabled ? (
        <View style={styles.ticketFormField}>
          <Text style={styles.ticketFormLabel}>Descripción pública</Text>
          <TextInput
            testID="catalog.form.public.description"
            style={[styles.ticketFormInput, styles.notesMultiline]}
            value={draft.publicDescription}
            onChangeText={onPublicDescriptionChange ?? (() => {})}
            placeholder="ej. Medallón casero, cheddar y pan de papa"
            placeholderTextColor={ticketColors.ink3}
            multiline
          />
          <Text style={[styles.ticketFormLabel, { fontSize: 11, opacity: 0.7, fontWeight: '400', marginTop: 4 }]}>
            Se ve en la tarjeta del producto, sin abrirlo. Ideal: 1–2 oraciones (~160 caracteres). La dirección del local
            ya se muestra aparte — no la repitas acá.
          </Text>
        </View>
      ) : null}
      {showOrderQtyFields ? (
        <View style={mobile ? styles.fieldGridMobile : styles.fieldGrid}>
          <View style={[styles.ticketFormField, !mobile && styles.fieldGridItem]}>
            <Text style={styles.ticketFormLabel}>Cantidad mínima de venta ({unitLabel})</Text>
            <TextInput
              testID="catalog.form.min-order-qty"
              style={styles.ticketFormInput}
              value={draft.minOrderQty}
              onChangeText={onMinOrderQtyChange ?? (() => {})}
              placeholder="Sin mínimo"
              placeholderTextColor={ticketColors.ink3}
              keyboardType="decimal-pad"
            />
            <Text style={[styles.ticketFormLabel, { fontSize: 11, opacity: 0.7, fontWeight: '400', marginTop: 4 }]}>
              Si lo dejás vacío, el cliente puede pedir cualquier cantidad.
            </Text>
          </View>
          <View style={[styles.ticketFormField, !mobile && styles.fieldGridItem]}>
            <Text style={styles.ticketFormLabel}>Cantidad máxima de venta ({unitLabel})</Text>
            <TextInput
              testID="catalog.form.max-order-qty"
              style={styles.ticketFormInput}
              value={draft.maxOrderQty}
              onChangeText={onMaxOrderQtyChange ?? (() => {})}
              placeholder="Sin máximo"
              placeholderTextColor={ticketColors.ink3}
              keyboardType="decimal-pad"
            />
          </View>
        </View>
      ) : null}
    </View>
  );
}
